export const VERTICAL_CODES = [
  "general",
  "skilled_nursing",
  "public_affairs",
  "technology",
  "professional_services",
  "nonprofit",
  "real_estate",
  "financial_services",
  "home_services",
  "education",
] as const;

export type VerticalCode = typeof VERTICAL_CODES[number];

export type VerticalCategory = {
  code: string;
  label: string;
  color: string;
};

export type VerticalProfile = {
  code: VerticalCode;
  name: string;
  description: string;
  sourceLabelSingular: string;
  sourceLabelPlural: string;
  leadLabelSingular: string;
  leadLabelPlural: string;
  sourceCategories: readonly VerticalCategory[];
};

const colors = ["#4D058F", "#087F7A", "#D45B6B", "#8668A2", "#00A99C", "#B56A2D", "#426B9B", "#746C7C", "#5F7A42", "#9B4D70", "#4C778A"];
const categories = (...labels: string[]): VerticalCategory[] => labels.map((label, index) => ({
  code: label.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""),
  label,
  color: colors[index % colors.length],
}));

export const VERTICAL_CATALOG: readonly VerticalProfile[] = [
  {
    code: "general", name: "General relationship management",
    description: "A flexible starting point for teams that manage many kinds of external relationships.",
    sourceLabelSingular: "Relationship source", sourceLabelPlural: "Relationship sources",
    leadLabelSingular: "Individual lead", leadLabelPlural: "Individual leads",
    sourceCategories: [
      { code: "customer", label: "Customer", color: colors[0] },
      { code: "professional", label: "Professional partner", color: colors[1] },
      { code: "community", label: "Community organization", color: colors[2] },
      { code: "vendor", label: "Vendor", color: colors[3] },
      { code: "other", label: "Other", color: colors[4] },
    ],
  },
  {
    code: "skilled_nursing", name: "Skilled Nursing Facilities",
    description: "Hospital, provider, payer, senior-living, and community relationships that drive resident referrals.",
    sourceLabelSingular: "Referral source", sourceLabelPlural: "Referral sources",
    leadLabelSingular: "Prospective resident", leadLabelPlural: "Prospective residents",
    sourceCategories: categories("Hospital / health system", "Physician / clinic", "Skilled nursing facility", "Assisted living", "Home health", "Hospice", "Case management", "Insurer / payer", "Senior living advisor", "Community organization", "Other"),
  },
  {
    code: "public_affairs", name: "Public Affairs",
    description: "Government, advocacy, media, coalition, and community stakeholders organized for sustained engagement.",
    sourceLabelSingular: "Stakeholder organization", sourceLabelPlural: "Stakeholder organizations",
    leadLabelSingular: "Engagement lead", leadLabelPlural: "Engagement leads",
    sourceCategories: categories("Elected office", "Government agency", "Advocacy organization", "Trade association", "Community organization", "Media outlet", "Coalition", "Donor / funder", "Business stakeholder", "Other"),
  },
  {
    code: "technology", name: "Technology Companies",
    description: "Customers, channel partners, investors, communities, and ecosystem relationships for growth teams.",
    sourceLabelSingular: "Growth source", sourceLabelPlural: "Growth sources",
    leadLabelSingular: "Sales lead", sourceCategories: categories("Customer", "Prospect organization", "Channel partner", "Technology partner", "Investor", "Accelerator / incubator", "Analyst / media", "Developer community", "Vendor", "Other"),
    leadLabelPlural: "Sales leads",
  },
  {
    code: "professional_services", name: "Professional Services",
    description: "Client, advisor, association, and peer-firm relationships for consultancies and practices.",
    sourceLabelSingular: "Referral partner", sourceLabelPlural: "Referral partners",
    leadLabelSingular: "Client lead", leadLabelPlural: "Client leads",
    sourceCategories: categories("Client", "Former client", "Professional advisor", "Peer firm", "Industry association", "Community organization", "Vendor", "Other"),
  },
  {
    code: "nonprofit", name: "Nonprofits & Foundations",
    description: "Community, funder, program, volunteer, and civic partnerships around a shared mission.",
    sourceLabelSingular: "Community partner", sourceLabelPlural: "Community partners",
    leadLabelSingular: "Constituent lead", leadLabelPlural: "Constituent leads",
    sourceCategories: categories("Funder / foundation", "Community organization", "Government agency", "Corporate partner", "Program partner", "Volunteer group", "Faith organization", "Media", "Other"),
  },
  {
    code: "real_estate", name: "Real Estate",
    description: "Broker, lender, builder, community, and past-client relationships that generate opportunities.",
    sourceLabelSingular: "Lead source", sourceLabelPlural: "Lead sources",
    leadLabelSingular: "Client lead", leadLabelPlural: "Client leads",
    sourceCategories: categories("Past client", "Real estate agent", "Brokerage", "Mortgage lender", "Builder / developer", "Property manager", "Title / closing", "Community organization", "Digital campaign", "Other"),
  },
  {
    code: "financial_services", name: "Financial Services",
    description: "Client and professional-referral relationships for advisory, lending, insurance, and banking teams.",
    sourceLabelSingular: "Referral partner", sourceLabelPlural: "Referral partners",
    leadLabelSingular: "Client lead", leadLabelPlural: "Client leads",
    sourceCategories: categories("Client", "Accountant", "Attorney", "Bank / credit union", "Insurance professional", "Real estate professional", "Employer", "Community organization", "Digital campaign", "Other"),
  },
  {
    code: "home_services", name: "Home Services",
    description: "Customer, trade, property, neighborhood, and campaign sources for local service businesses.",
    sourceLabelSingular: "Lead source", sourceLabelPlural: "Lead sources",
    leadLabelSingular: "Customer lead", leadLabelPlural: "Customer leads",
    sourceCategories: categories("Past customer", "Trade partner", "Property manager", "Real estate professional", "Manufacturer / distributor", "Neighborhood group", "Digital campaign", "Event", "Other"),
  },
  {
    code: "education", name: "Education",
    description: "School, counselor, employer, alumni, and community relationships that support enrollment and programs.",
    sourceLabelSingular: "Community partner", sourceLabelPlural: "Community partners",
    leadLabelSingular: "Prospective student", leadLabelPlural: "Prospective students",
    sourceCategories: categories("School / district", "Counselor", "Employer", "Alumni", "Community organization", "Government agency", "Education partner", "Event", "Digital campaign", "Other"),
  },
] as const;

export function verticalForCode(code: string | null | undefined): VerticalProfile {
  return VERTICAL_CATALOG.find((profile) => profile.code === code) ?? VERTICAL_CATALOG[0];
}
